import { ArrowRight, Check, Monitor, Smartphone } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { DEV_FEATURES } from "../data/content";

export const DevSection = ({ onEnquire }) => (
  <section id="development" data-testid="dev-section" className="noise-overlay relative overflow-hidden bg-navy-950 py-24 lg:py-32">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(212,175,55,0.06),transparent_55%)]" />
    <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-8 lg:grid-cols-2 lg:px-16">
      <div>
        <SectionHeading
          overline="Websites & Apps"
          testId="dev-heading"
          title={
            <>
              Digital products that make you look <span className="text-gold-metallic italic">established</span> from day one
            </>
          }
          description="Your website is often the first meeting a customer has with your business. We design and build modern, responsive websites and custom applications that earn trust in seconds — and turn visitors into enquiries."
        />
        <ul className="mt-10 space-y-4">
          {DEV_FEATURES.map((f, i) => (
            <Reveal key={f} delay={0.06 * i}>
              <li className="flex items-start gap-3.5 text-sm sm:text-base text-slate-300">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
                  <Check className="h-3.5 w-3.5 text-gold" />
                </span>
                {f}
              </li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={0.35}>
          <a
            href="#contact"
            data-testid="dev-cta"
            onClick={() => onEnquire("Website Development")}
            className="btn-gold mt-10 inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy-950 transition-[box-shadow,transform] duration-400 hover:-translate-y-1 hover:shadow-[0_18px_50px_-10px_rgba(212,175,55,0.55)]"
          >
            Start Your Project
            <ArrowRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="relative">
          <div aria-hidden="true" className="absolute -inset-6 rounded-[2.5rem] bg-gold/8 blur-3xl" />
          <div className="sheen-border relative overflow-hidden rounded-3xl border border-gold/20 bg-navy-900">
            <div className="flex items-center gap-2 border-b border-white/8 px-5 py-3.5">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-600" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-600" />
              <span className="h-2.5 w-2.5 rounded-full bg-gold/70" />
              <span className="ml-4 flex-1 rounded-full bg-navy-950/80 px-4 py-1 font-mono text-[10px] tracking-wider text-slate-500">
                yourbusiness.ae
              </span>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1579525612525-053cd3e8cbd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMGJ1c2luZXNzJTIwc2t5bGluZSUyMGx1eHVyeSUyMGNvcnBvcmF0ZSUyMG9mZmljZXxlbnwwfHx8Ymx1ZXwxNzg5NTczMjg4fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern website showcase with Dubai architecture"
                className="h-[380px] w-full object-cover opacity-90"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/30 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 grid grid-cols-3 gap-3">
                {["Responsive", "Fast", "SEO-Ready"].map((chip, i) => (
                  <div
                    key={chip}
                    className="animate-float-y rounded-xl glass-panel px-3 py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-gold-soft"
                    style={{ animationDelay: `${i * 0.9}s` }}
                  >
                    {chip}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="animate-float-y absolute -left-5 -top-5 flex items-center gap-2.5 rounded-2xl glass-panel px-4 py-3" style={{ animationDelay: "1.2s" }}>
            <Monitor className="h-5 w-5 text-gold" />
            <span className="text-xs font-semibold text-slate-200">Websites</span>
          </div>
          <div className="animate-float-y absolute -right-4 top-1/2 flex items-center gap-2.5 rounded-2xl glass-panel px-4 py-3" style={{ animationDelay: "2.4s" }}>
            <Smartphone className="h-5 w-5 text-gold" />
            <span className="text-xs font-semibold text-slate-200">Mobile Apps</span>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
